import { Card, CardHeader, parseMoneyInput, parsePercentInput, minorToInput, bpToPercent, useToast } from "@helmr/ui";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import type { ApiClient } from "../../api/client";
import { CsvInput, Field, FormSkeleton, Load, mapError, SavedBar, useDirtyGuard, useScope, type Mapped } from "./kit";
import s from "./settings.module.css";
import { t } from "./strings";

type Draft = Record<string, unknown>;

export interface CustomProps { value: unknown; onChange: (v: unknown) => void; error?: string; disabled: boolean; id: string }

export type FieldDef = {
  /** Unique within the form; also the key an API error message is matched against (a dotted path for nested values). */
  key: string;
  path: readonly string[];
  label: string;
  hint?: string;
  full?: boolean;
  /** text/int/percent/money/… are typed as strings in the field and converted once, on save. */
  kind: "text" | "int" | "bool" | "select" | "percent" | "money" | "list" | "channels" | "custom";
  nullable?: boolean;
  upper?: boolean;
  options?: ReadonlyArray<{ value: string; label: string }>;
  /** Reads the field's value from the loaded object when it is not at `path` (a field that spans several keys). */
  read?: (data: never) => unknown;
  /** The edited object is merged into the top level of the patch instead of set at `path`. */
  spread?: boolean;
  render?: (p: CustomProps) => ReactNode;
  placeholder?: string;
};

const getPath = (o: unknown, path: readonly string[]): unknown => path.reduce<unknown>((acc, k) => (acc && typeof acc === "object" ? (acc as Record<string, unknown>)[k] : undefined), o);
function setPath(target: Record<string, unknown>, path: readonly string[], value: unknown) {
  let cur = target;
  path.slice(0, -1).forEach((k) => { cur = (cur[k] ??= {}) as Record<string, unknown>; });
  cur[path[path.length - 1]!] = value;
}

function toDraft(f: FieldDef, raw: unknown, currency: string): unknown {
  switch (f.kind) {
    case "bool": return raw === true;
    case "list": case "channels": return Array.isArray(raw) ? raw : [];
    case "custom": return raw ?? null;
    case "percent": return typeof raw === "number" ? bpToPercent(raw) : "";
    case "money": return typeof raw === "number" ? minorToInput(raw, currency) : "";
    default: return raw === null || raw === undefined ? "" : String(raw);
  }
}

const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

/** Draft values to the patch body, or the local field errors that stop the save. */
export function buildPatch(fields: readonly FieldDef[], draft: Draft, initial: Draft, currency: string, sendAll = false): { patch: Record<string, unknown>; errors: Record<string, string> } {
  const patch: Record<string, unknown> = {};
  const errors: Record<string, string> = {};
  for (const f of fields) {
    if (!sendAll && same(draft[f.key], initial[f.key])) continue;
    const v = draft[f.key];
    let out: unknown;
    if (f.kind === "bool" || f.kind === "custom" || f.kind === "channels") out = v;
    else if (f.kind === "list") out = v;
    else {
      const text = String(v ?? "").trim();
      if (text === "") {
        if (f.nullable) out = null; else { errors[f.key] = `${f.label} is required`; continue; }
      } else if (f.kind === "int") {
        if (!/^\d+$/.test(text)) { errors[f.key] = "Enter a whole number"; continue; }
        out = Number(text);
      } else if (f.kind === "percent") {
        const r = parsePercentInput(text);
        if (!r.ok) { errors[f.key] = r.reason === "too_many_decimals" ? "Use at most two decimals" : "Enter a percentage such as 18"; continue; }
        out = r.bp;
      } else if (f.kind === "money") {
        const r = parseMoneyInput(text, currency);
        if (!r.ok) { errors[f.key] = r.reason === "too_many_decimals" ? "Too many decimals for this currency" : r.reason === "negative" ? "Cannot be negative" : "Enter an amount such as 1,499"; continue; }
        out = r.minor;
      } else out = f.upper ? text.toUpperCase() : text;
    }
    if (f.spread && out && typeof out === "object") Object.assign(patch, out);
    else setPath(patch, f.path, out);
  }
  return { patch, errors };
}

/**
 * One settings section: load it, edit locally, save with one PATCH of the changed keys. The dirty state blocks navigation with a confirm.
 * API validation (`400`, or `422`) lands on the field whose key the message names; the rest shows above the Save bar.
 */
export function SectionForm<T extends object>({ title, subtitle, what, queryKey, load, save, fields, canEdit, currency, note, after, onSaved, sendAll, blankForm }: {
  title: string; subtitle?: string; what: string; queryKey: string;
  load: (c: ApiClient) => Promise<T>; save: (c: ApiClient, patch: Record<string, unknown>) => Promise<T>;
  fields: readonly FieldDef[]; canEdit: boolean; currency?: string; note?: ReactNode; after?: ReactNode; onSaved?: (next: T) => void;
  /** Create forms send every field, not only the ones that changed. */
  sendAll?: boolean;
  /** `load` returns an empty record to fill in: never cached, or the next create form opens on the record just created. */
  blankForm?: boolean;
}) {
  const sc = useScope();
  const toast = useToast();
  const cur = currency ?? sc.ctx.me?.business.currency ?? "";
  const key = sc.key(queryKey);
  const q = useQuery({ queryKey: key, queryFn: () => load(sc.client), retry: false, ...(blankForm ? { gcTime: 0, staleTime: 0 } : {}) });
  return (
    <Card aria-label={title}>
      <CardHeader title={title} subtitle={subtitle} />
      <Load query={q} what={what} skeleton={<FormSkeleton rows={Math.min(fields.length, 6)} />}>
        {(data) => <Inner data={data} fields={fields} canEdit={canEdit} currency={cur} note={note}
          onSave={async (patch) => { const next = await save(sc.client, patch); if (!blankForm) sc.qc.setQueryData(key, next); toast.show(t.saved); return next; }} onSaved={onSaved} sendAll={sendAll} />}
      </Load>
      {after}
    </Card>
  );
}

function Inner<T extends object>({ data, fields, canEdit, currency, note, onSave, onSaved, sendAll }: {
  data: T; fields: readonly FieldDef[]; canEdit: boolean; currency: string; note?: ReactNode; onSave: (patch: Record<string, unknown>) => Promise<T>; onSaved?: (next: T) => void; sendAll?: boolean;
}) {
  const initial = useMemo(() => Object.fromEntries(fields.map((f) => [f.key, toDraft(f, f.read ? f.read(data as never) : getPath(data, f.path), currency)])) as Draft, [data, fields, currency]);
  const [draft, setDraft] = useState<Draft>(initial);
  const [errors, setErrors] = useState<Mapped>({ fields: {}, form: null });
  // Only new data resets the draft. `fields` is rebuilt whenever the page renders, and that must not wipe what is being typed.
  useEffect(() => setDraft(initial), [data, currency]); // eslint-disable-line react-hooks/exhaustive-deps
  const dirty = fields.some((f) => !same(draft[f.key], initial[f.key]));
  const { node: guard, release } = useDirtyGuard(dirty);
  const keys = fields.map((f) => f.key);

  const m = useMutation({
    mutationFn: (patch: Record<string, unknown>) => onSave(patch),
    onSuccess: (next) => { setErrors({ fields: {}, form: null }); if (onSaved) { release(); onSaved(next); } },
    onError: (e) => setErrors(mapError(e, keys)),
  });

  function submit(e: React.FormEvent) {
    e.preventDefault();
    const { patch, errors: local } = buildPatch(fields, draft, initial, currency, sendAll);
    if (Object.keys(local).length) { setErrors({ fields: local, form: t.validation }); return; }
    setErrors({ fields: {}, form: null });
    m.mutate(patch);
  }
  const set = (k: string, v: unknown) => setDraft((d) => ({ ...d, [k]: v }));

  return (
    <form className={s.form} onSubmit={submit} noValidate>
      {guard}
      {note ? <div className={s.note}>{note}</div> : null}
      <div className={s.fields}>
        {fields.map((f) => <FieldView key={f.key} f={f} value={draft[f.key]} error={errors.fields[f.key]} disabled={!canEdit} onChange={(v) => set(f.key, v)} />)}
      </div>
      {errors.form ? <div className={s.formErr} role="alert">{errors.form}</div> : null}
      <SavedBar dirty={dirty} alwaysSave={sendAll} busy={m.isPending} canEdit={canEdit} onDiscard={() => { setDraft(initial); setErrors({ fields: {}, form: null }); }} />
    </form>
  );
}

export function FieldView({ f, value, error, disabled, onChange }: { f: FieldDef; value: unknown; error?: string; disabled: boolean; onChange: (v: unknown) => void }) {
  const id = `f-${f.key}`;
  const invalid = error ? true : undefined;
  let control: ReactNode;
  switch (f.kind) {
    case "bool":
      control = <label className={s.check}><input id={id} type="checkbox" checked={value === true} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />{f.hint ?? "On"}</label>;
      return <Field label={f.label} error={error} full={f.full}>{control}</Field>;
    case "select":
      control = <select id={id} className={s.select} value={String(value ?? "")} disabled={disabled} aria-invalid={invalid} onChange={(e) => onChange(e.target.value)}>
        {f.nullable || value === "" ? <option value="">{f.nullable ? "Default" : "Choose…"}</option> : null}
        {f.options?.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>;
      break;
    case "channels": case "list": {
      if (f.kind === "list") {
        control = <CsvInput id={id} value={value as string[]} disabled={disabled} aria-invalid={invalid} onChange={onChange} />;
        break;
      }
      const chosen = value as string[];
      control = (
        <div className={s.checks} role="group" aria-label={f.label}>
          {f.options?.map((o) => (
            <label key={o.value} className={s.check}>
              <input type="checkbox" checked={chosen.includes(o.value)} disabled={disabled} onChange={(e) => onChange(e.target.checked ? [...chosen, o.value] : chosen.filter((x) => x !== o.value))} />{o.label}
            </label>
          ))}
        </div>
      );
      return <Field label={f.label} hint={f.hint} error={error} full={f.full}>{control}</Field>;
    }
    case "custom":
      return <Field label={f.label} hint={f.hint} error={error} full={f.full}>{f.render!({ value, onChange, error, disabled, id })}</Field>;
    default:
      control = <input id={id} className={s.input} value={String(value ?? "")} disabled={disabled} aria-invalid={invalid} placeholder={f.placeholder}
        inputMode={f.kind === "int" ? "numeric" : f.kind === "money" || f.kind === "percent" ? "decimal" : undefined} onChange={(e) => onChange(e.target.value)} />;
  }
  return <Field label={f.label} hint={f.hint} error={error} full={f.full} htmlFor={id}>{control}</Field>;
}
